import { useCallback, useState } from 'react';

import type { Trim, Trims } from './attendance';
import { useAccountStorage } from '../../shared/account/context';
import type { KeyValueStorage } from '../../shared/account/storage';

/**
 * How much of each event the user is actually attending, in localStorage
 * beside the two lists and the travel choices.
 *
 * Keyed by event id alone rather than by day: an id already identifies one
 * occurrence, and folding the day in would only invent a way for the two to
 * disagree. Entries for events no longer on the schedule are left in place, so
 * removing an event and adding it back restores the trim rather than quietly
 * resetting it.
 */

const KEY = 'mcw.attendance';

function read(storage: KeyValueStorage): Trims {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};

    const out: Trims = {};
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      const { start, end } = value as Trim;
      const entry: Trim = {};
      if (typeof start === 'number' && Number.isFinite(start)) entry.start = start;
      if (typeof end === 'number' && Number.isFinite(end)) entry.end = end;
      if (Object.keys(entry).length > 0) out[id] = entry;
    }
    return out;
  } catch {
    return {};
  }
}

function write(trims: Trims, storage: KeyValueStorage): void {
  try {
    storage.setItem(KEY, JSON.stringify(trims));
  } catch {
    // Site data blocked. Trims still work for this session.
  }
}

export interface AttendanceStore {
  trims: Trims;
  setEdge: (id: string, edge: 'start' | 'end', minutes: number) => void;
  reset: (id: string) => void;
}

export function useAttendance(): AttendanceStore {
  /** localStorage, or the account once the user has one. */
  const storage = useAccountStorage();
  const [trims, setTrims] = useState<Trims>(() => read(storage));

  // The trims moved: start again from wherever they now live.
  const [source, setSource] = useState(storage);
  if (source !== storage) {
    setSource(storage);
    setTrims(read(storage));
  }

  const setEdge = useCallback((id: string, edge: 'start' | 'end', minutes: number) => {
    setTrims((prev) => {
      const next = { ...prev, [id]: { ...prev[id], [edge]: minutes } };
      write(next, storage);
      return next;
    });
  }, [storage]);

  const reset = useCallback((id: string) => {
    setTrims((prev) => {
      const next = { ...prev };
      delete next[id];
      write(next, storage);
      return next;
    });
  }, [storage]);

  return { trims, setEdge, reset };
}
